/* eslint-disable react/jsx-props-no-spreading */
import {
  Alert,
  Button,
  Card,
  CardActions,
  CardContent,
  LinearProgress,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMount, useUpdateEffect } from 'react-use';
import {
  SUBUSER_STATUS_HEADER_DATA,
  SUBUSER_STATUS_VIEW_LIST_LENGTHS,
  MODAL_TYPE_DELETE,
  MODAL_TYPE_UPDATE,
} from '../../common/consts';
import NewTable from '../../components/NewTable';
import SubUserModal from '../../components/subuser/SubUserModal';
import Toast from '../../components/Toast';
import {
  getData,
  openModal,
  closeModal,
  changeModalItem,
  searchRobot,
  updateCardItem,
  deleteCardItem,
} from '../../modules/subUser';

const SubUserStatusContainer = () => {
  const dispatch = useDispatch();
  const {
    loading,
    contentItems,
    total,
    list,
    modal,
    add: { itemResult },
  } = useSelector((state) => state.subuser);
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(
    SUBUSER_STATUS_VIEW_LIST_LENGTHS[0].value,
  );
  const [order, setOrder] = useState('desc');
  const [sort, setSort] = useState('date');
  const [snack, setSnack] = useState({ open: false });

  const onModalClick = (type, item) => {
    let items = item;
    if (!items) {
      items = selected.map((id) => list.find((li) => li.id === id));
    }
    dispatch(
      openModal({
        items,
        type,
      }),
    );
  };

  const handleAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = contentItems.map((n) => n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleCheckClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);

    const { type, name } = event.target;
    if (type === 'button' && name) {
      onModalClick(name, [list.find((li) => li.id === id)]);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSortClick = (property) => () => {
    if (property !== 'number') {
      const isAsc = sort === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setSort(property);
    }
  };

  const onItemUpdateClick = () => {
    onModalClick(MODAL_TYPE_UPDATE);
  };

  const onItemDeleteClick = () => {
    onModalClick(MODAL_TYPE_DELETE);
  };

  const handleModalCancel = () => {
    setSelected([]);
    dispatch(closeModal());
  };

  const handleModalConfirm = (data) => {
    if (modal.bDel) {
      dispatch(deleteCardItem(data));
    } else {
      dispatch(updateCardItem(data));
    }
  };

  const handleModalChange = (data) => {
    dispatch(changeModalItem(data));
  };

  const handleModalSearch = (id, value) => {
    dispatch(searchRobot({ type: 'update', id, value }));
  };

  useMount(() => {
    dispatch(
      getData({
        sort,
        desc: order === 'desc',
        skip: 0,
        limit: rowsPerPage,
      }),
    );
  });

  useUpdateEffect(() => {
    dispatch(
      getData({
        sort,
        desc: order === 'desc',
        skip: 0,
        limit: rowsPerPage,
      }),
    );
  }, [sort, order, rowsPerPage]);

  useUpdateEffect(() => {
    dispatch(
      getData({
        sort,
        desc: order === 'desc',
        skip: page * rowsPerPage,
        limit: rowsPerPage,
      }),
    );
  }, [page]);

  useUpdateEffect(() => {
    if (modal.result !== null) {
      const message = modal.result ? modal.message : modal.error;
      const severity = modal.result ? 'success' : 'error';
      setSnack({
        open: true,
        onClose: () => setSnack({ open: false }),
        message,
        severity,
      });

      if (modal.result) {
        setSelected([]);
        dispatch(closeModal());
        dispatch(
          getData({
            sort,
            desc: order === 'desc',
            skip: page * rowsPerPage,
            limit: rowsPerPage,
          }),
        );
      }
    }
    if (itemResult) {
      dispatch(
        getData({
          sort,
          desc: order === 'desc',
          skip: 0,
          limit: rowsPerPage,
        }),
      );
    }
  }, [modal.result, itemResult]);

  return (
    <Card>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ m: 2 }}
      >
        <Typography variant="h5">사용자 목록</Typography>
        {contentItems.length > 0 && (
          <CardActions>
            <Button
              color="secondary"
              variant="contained"
              size="small"
              disabled={selected.length === 0}
              onClick={onItemUpdateClick}
            >
              선택 수정
            </Button>
            <Button
              color="error"
              variant="contained"
              size="small"
              disabled={selected.length === 0}
              onClick={onItemDeleteClick}
            >
              선택 삭제
            </Button>
          </CardActions>
        )}
      </Box>
      <CardContent>
        <Box sx={{ width: '100%' }}>
          {loading && <LinearProgress />}
          {!loading && contentItems.length === 0 && (
            <Alert color="warning">등록된 사용자가 없습니다.</Alert>
          )}
        </Box>
        {contentItems.length > 0 && (
          <NewTable
            {...{
              selectable: true,
              name: 'subuser_status',
              headerItems: SUBUSER_STATUS_HEADER_DATA,
              contentItems,
              lengthOptions: SUBUSER_STATUS_VIEW_LIST_LENGTHS,
              total,
              rowsPerPage,
              page,
              selected,
              handleAllClick,
              handleCheckClick,
              handleChangePage,
              handleChangeRowsPerPage,
              handleSortClick,
              order,
              orderBy: sort,
            }}
          />
        )}
      </CardContent>
      <SubUserModal
        {...{
          ...modal,
          onSearch: handleModalSearch,
          onChange: handleModalChange,
          onCancel: handleModalCancel,
          onUpdate: handleModalConfirm,
        }}
      />
      <Toast {...{ ...snack }} />
    </Card>
  );
};

export default SubUserStatusContainer;
